/** Staff: every installer's portfolio projects (plus the approval-stage project) in one gallery. */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Images, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { ProjectCard, BRAND_STYLE } from '@/components/portfolio/ProjectCard';

export default function StaffPortfolios() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [data, setData] = useState({ projects: [], installers: [] });
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState('all');
  const [installer, setInstaller] = useState('all');
  const [q, setQ] = useState('');
  useEffect(() => { if (!isStaff) navigate('/'); }, [isStaff, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (brand !== 'all') qs.append('brand', brand);
      if (installer !== 'all') qs.append('user_id', installer);
      if (q.trim()) qs.append('q', q.trim());
      const res = await fetch(`${API_URL}/api/staff/portfolio?${qs}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load portfolios');
      setData(await res.json());
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [brand, installer, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  if (!isStaff) return null;
  const added = data.projects.filter((p) => p.source === 'portfolio').length;
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg shrink-0" data-testid="portfolios-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><Images className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Installer Portfolios</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Every project installers have added — newest first. Photos may be used for Ox Resin marketing.</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3"><Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span></Button>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="portfolios-filters">
          {['all', ...Object.keys(BRAND_STYLE)].map((b) => (
            <button key={b} onClick={() => setBrand(b)} className={`px-3 py-2 rounded-lg text-sm font-bold border-2 ${brand === b ? 'bg-gray-900 text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`} data-testid={`portfolios-brand-${b}`}>{b === 'all' ? 'All brands' : BRAND_STYLE[b].label}</button>
          ))}
          <select value={installer} onChange={(e) => setInstaller(e.target.value)} className="h-10 border-2 border-gray-200 rounded-lg px-2 bg-white text-sm font-semibold" data-testid="portfolios-installer">
            <option value="all">All installers</option>
            {data.installers.map((u) => <option key={u.user_id} value={u.user_id}>{u.company_name || u.name || u.email}{u.account_code ? ` · ${u.account_code}` : ''}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project, address, material, installer…" className="pl-9 h-10" data-testid="portfolios-search" /></div>
        </div>
        <div className="text-xs text-gray-500" data-testid="portfolios-count">{loading ? 'Loading…' : `${data.projects.length} project${data.projects.length === 1 ? '' : 's'} · ${added} added by installers · ${data.projects.length - added} from approvals`}</div>
        {!loading && data.projects.length === 0 ? (
          <Card className="p-10 text-center text-gray-500 italic" data-testid="portfolios-empty">No portfolio projects match these filters.</Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {data.projects.map((p) => <ProjectCard key={p.id} project={p} showInstaller />)}
          </div>
        )}
      </div>
    </div>
  );
}
